import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { fetchProducts } from '../requests';
import { formatPrice } from '../utils';
import { KRAJ } from '../kraj';
import { KartaZachety } from './auth/BramkaLogowania';
import { useLimitBezKonta } from '../hooks/useLimitBezKonta';
import { zdarzenie } from '../lib/pomiar';

/** „Zostały 2 z 3 podglądów historii ceny” (FE-09). */
export const tekstPodgladow = (zostalo, limit) => {
  if (zostalo <= 0) return `To był ostatni z ${limit} bezpłatnych podglądów historii ceny`;
  if (zostalo === 1) return `Został 1 z ${limit} podglądów historii ceny`;
  return `Zostały ${zostalo} z ${limit} podglądów historii ceny`;
};

// Historia najniższej ceny modelu (panele: zł + zł/Wp; falowniki/magazyny: zł).
const ENDPOINT = {
  panel: 'get_panel_model_history',
  inwerter: 'get_inverter_model_history',
  magazyn: 'get_magazine_model_history',
};
const PANEL_W = 440; // px, szerokość okienka (spójna z clampem)

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getFullYear()).slice(2)}`;
};
const fmtFull = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(KRAJ.locale);
};
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// Pływające, przeciągalne, zamykalne okienko z wykresem historii ceny.
const PriceHistoryChart = ({ modelId, category = 'panel', modelName = '', open = false, onClose }) => {
  const { t } = useTranslation('model');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState('zl'); // 'zl' | 'wp'
  const [pos, setPos] = useState(null);        // { x, y } — null zanim policzymy pozycję startową

  const panelRef = useRef(null);
  const dragRef = useRef(null); // { dx, dy } offset kursora względem lewego-górnego rogu okienka

  const showWp = category === 'panel';
  const endpoint = ENDPOINT[category];

  // Historia cen bez konta (FE-09, decyzja właściciela 2026-10-19): 3 otwarcia wykresu w tej przeglądarce
  // z licznikiem, potem zachęta do darmowego konta; zalogowany bez limitu. Wcześniej wykres wisiał na
  // wspólnym wyłączniku bramek (config/bramki.js), teraz o dostępie decyduje sam licznik (lib/limitBezKonta).
  // Użycie liczymy przy otwarciu okienka (klik), nigdy przy wejściu na stronę: roboty i prerender go nie
  // otwierają. Okienko ma 440 px szerokości, więc zamiast wykresu stoi sama zachęta (tryb `zamiast`).
  const limit = useLimitBezKonta('historiaCen');
  const location = useLocation();
  const limitRef = useRef(limit);
  limitRef.current = limit;
  // `null`: jeszcze nie rozstrzygnięte dla tego otwarcia. Ref otwarcia chroni przed podwójnym liczeniem
  // (podwójny efekt w trybie StrictMode, ponowny render z tym samym modelem).
  const [odblokowane, setOdblokowane] = useState(null);
  const otwarcie = useRef(null);
  useEffect(() => {
    if (!open) { otwarcie.current = null; setOdblokowane(null); return; }
    const klucz = `${category}:${modelId}`;
    if (otwarcie.current === klucz) return;
    otwarcie.current = klucz;
    const wolno = limitRef.current.bezLimitu || limitRef.current.sprobuj();
    setOdblokowane(wolno);
    // Pomiar otwarć (2026-11-02). Zablokowane limitem liczy `limit_bez_konta` w oknie rejestracji.
    if (wolno && ENDPOINT[category] && modelId != null) zdarzenie('historia_cen', klucz);
  }, [open, category, modelId]);
  const authLoading = odblokowane === null;

  // Utrzymanie okienka w granicach ekranu (z 8px marginesem).
  const clampPos = useCallback((x, y) => {
    const el = panelRef.current;
    const w = el ? el.offsetWidth : PANEL_W;
    const h = el ? el.offsetHeight : 320;
    return {
      x: clamp(x, 8, Math.max(8, window.innerWidth - w - 8)),
      y: clamp(y, 8, Math.max(8, window.innerHeight - h - 8)),
    };
  }, []);

  const onPointerMove = useCallback((e) => {
    if (!dragRef.current) return;
    const { dx, dy } = dragRef.current;
    setPos(clampPos(e.clientX - dx, e.clientY - dy));
  }, [clampPos]);

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
  }, [onPointerMove]);

  const onHeaderPointerDown = (e) => {
    // Klik w przycisk (toggle / zamknij) nie zaczyna przeciągania.
    if (e.target.closest('button')) return;
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  // Fetch danych przy otwarciu — TYLKO dla zalogowanych. Zasłona w interfejsie
  // nie wystarcza: bez tego warunku historia cen jechałaby do przeglądarki
  // każdego gościa i wystarczyłaby zakładka „Sieć", żeby ją odczytać.
  useEffect(() => {
    if (!open || !endpoint || modelId == null || !odblokowane) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const data = await fetchProducts(`${endpoint}/${modelId}`);
      if (!cancelled) {
        setHistory(Array.isArray(data) ? data : []);
        setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, endpoint, modelId, odblokowane]);

  // Pozycja startowa: prawy-górny obszar, w granicach ekranu.
  useEffect(() => {
    if (!open) return;
    const x = clamp(window.innerWidth - PANEL_W - 40, 8, Math.max(8, window.innerWidth - PANEL_W - 8));
    setPos({ x, y: 110 });
  }, [open]);

  // Reclamp przy zmianie rozmiaru okna.
  useEffect(() => {
    if (!open) return undefined;
    const onResize = () => setPos((p) => (p ? clampPos(p.x, p.y) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, clampPos]);

  // Esc zamyka.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Sprzątanie globalnych listenerów drag przy odmontowaniu.
  useEffect(() => () => {
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
  }, [onPointerMove, onPointerUp]);

  if (!open || !endpoint) return null;

  const useWp = metric === 'wp' && showWp;
  const data = history
    .map((h) => ({ label: fmtDate(h.captured_at), value: useWp ? h.price_wp_min : h.price_min, raw: h }))
    .filter((d) => d.value != null);

  const lastChange = history.length ? history[history.length - 1].captured_at : null;
  const unit = useWp ? `${KRAJ.symbol}/Wp` : KRAJ.symbol;
  const dec = useWp ? 2 : 0;

  return (
    <div
      ref={panelRef}
      style={pos ? { left: pos.x, top: pos.y } : { visibility: 'hidden' }}
      className="fixed z-50 w-[440px] max-w-[calc(100vw-16px)] bg-white rounded-2xl border border-gray-200 shadow-2xl"
    >
      {/* Nagłówek = uchwyt przeciągania */}
      <div
        onPointerDown={onHeaderPointerDown}
        className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 cursor-move select-none touch-none"
      >
        <div className="min-w-0">
          <h2 className="text-sm font-bold text-gray-900">{t('historia_ceny')}</h2>
          {modelName && <p className="text-xs text-gray-400 truncate mt-0.5">{modelName}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {showWp && (
            <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-xs">
              <button
                type="button"
                onClick={() => setMetric('zl')}
                className={`px-2.5 py-1 font-semibold ${!useWp ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
              >{KRAJ.symbol}</button>
              <button
                type="button"
                onClick={() => setMetric('wp')}
                className={`px-2.5 py-1 font-semibold border-l border-gray-200 ${useWp ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
              >{`${KRAJ.symbol}/Wp`}</button>
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label={t('wykres.zamknij')}
            className="h-7 w-7 inline-flex items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      </div>

      {/* Treść */}
      <div className="px-5 pb-4">
        {authLoading ? (
          <p className="text-sm text-gray-400 py-8 text-center">{t('wykres.wczytywanie')}</p>
        ) : !odblokowane ? (
          <KartaZachety
            kompaktowa
            tytul={`Wykorzystano ${limit.limit} bezpłatne podglądy historii ceny`}
            opis="Na darmowym koncie historię cen oglądasz bez limitu."
          />
        ) : loading ? (
          <p className="text-sm text-gray-400 py-8 text-center">{t('wykres.wczytywanie')}</p>
        ) : data.length < 2 ? (
          <p className="text-sm text-gray-400 py-8 text-center">
            {t('wykres.za_malo_danych')}
          </p>
        ) : (
          <>
            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  {/* Recharts maluje w SVG poza Tailwindem, więc kolory idą tu wprost —
                      ale ze zmiennych motywu (src/index.css), a nie z literałów.
                      Inaczej w ciemnym motywie siatka świeciłaby na biało, a dymek
                      zostałby białym prostokątem. */}
                  <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--vv-bd-gray-200))" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: 'rgb(var(--vv-tx-gray-400))' }} axisLine={false} tickLine={false} />
                  <YAxis
                    tick={{ fontSize: 12, fill: 'rgb(var(--vv-tx-gray-400))' }} axisLine={false} tickLine={false} width={56}
                    tickFormatter={(v) => `${formatPrice(v, dec)} ${unit}`}
                  />
                  <Tooltip
                    formatter={(v) => [`${formatPrice(v, dec)} ${unit}`, t('wykres.cena_od')]}
                    labelStyle={{ color: 'rgb(var(--vv-tx-gray-500))' }}
                    itemStyle={{ color: 'rgb(var(--vv-tx-gray-700))' }}
                    contentStyle={{
                      borderRadius: 12,
                      background: 'rgb(var(--vv-bg-white))',
                      border: '1px solid rgb(var(--vv-bd-gray-200))',
                      fontSize: 13,
                    }}
                  />
                  <Line type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={2} dot={{ r: 3, fill: '#2563eb' }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] text-gray-400">
              <span>{lastChange ? t('wykres.ostatnia_zmiana', { data: fmtFull(lastChange) }) : ''}</span>
              <span>{t('wykres.aktualizacja')}</span>
            </div>
          </>
        )}
        {/* Licznik bez konta pod wykresem (także przy „za mało danych”): ile podglądów zostało. */}
        {odblokowane && !limit.bezLimitu && (
          <p className="mt-2 text-center text-xs text-gray-500">
            {tekstPodgladow(limit.zostalo, limit.limit)}.{' '}
            <Link to="/rejestracja" state={{ from: location.pathname + location.search }}
                  className="font-semibold text-blue-700 hover:underline">Załóż darmowe konto</Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default PriceHistoryChart;
